import { useEffect, useRef, useState } from "react";
import { FlaskConical, Lock, LogOut, MoreVertical, Settings, ShieldCheck } from "lucide-react";

const ROLE_LABEL = {
  student: "Élève",
  tutor: "Tuteur",
  teacher: "Enseignant",
  parent: "Parent",
  admin: "Administrateur",
};

export function initials(name) {
  const words = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "?";
  if (words.length === 1) return words[0][0].toUpperCase();
  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
}

/**
 * Logged-in account control: one pill (initials + ⋮) that opens the account
 * menu. Name and role sit next to it on desktop.
 */
const AccountMenu = ({ session, who, onLogout, onOpenSettings, onOpenSimulator, onOpenAdmin, onChangePassword }) => {
  const [open, setOpen] = useState(false);
  const [lang] = useState("fr");
  const [langHint, setLangHint] = useState(false);
  const ref = useRef(null);
  const isAdmin = session.role === "admin";

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!ref.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const run = (action) => () => {
    setOpen(false);
    action?.();
  };

  const item =
    "flex w-full items-center gap-3 px-5 py-2.5 text-left text-[15px] font-medium text-[#122118] hover:bg-[#F3FCF7] focus:bg-[#F3FCF7] focus:outline-none";
  const icon = "h-[18px] w-[18px] shrink-0 text-[#4a5f53]";
  const items = [
    isAdmin && { id: "admin", label: "Console admin", Icon: ShieldCheck, action: onOpenAdmin },
    !isAdmin && { id: "settings", label: "Réglages", Icon: Settings, action: onOpenSettings },
    { id: "simulator", label: "Simulateur", Icon: FlaskConical, action: onOpenSimulator },
    { id: "password", label: "Changer le mot de passe", Icon: Lock, action: onChangePassword },
  ].filter(Boolean);

  return (
    <div className="flex items-center gap-3" ref={ref}>
      <div className="hidden text-right leading-tight md:block" data-testid="account-identity">
        <p className="max-w-[14rem] truncate text-sm font-bold text-[#122118]">{who}</p>
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#829C8D]">
          {ROLE_LABEL[session.role] || "Parent"}
        </p>
      </div>
      <div className="relative">
        <button
          type="button"
          data-testid="account-pill"
          aria-expanded={open}
          aria-haspopup="menu"
          aria-label={`Compte de ${who}`}
          onClick={() => setOpen((value) => !value)}
          className={`inline-flex h-11 items-center gap-1 rounded-full border bg-white py-1 pl-1 pr-2 transition-colors ${
            open ? "border-[#25D366] shadow-[0_0_0_3px_rgba(37,211,102,0.15)]" : "border-gray-200 hover:border-[#25D366]"
          }`}
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#25D366] text-[13px] font-bold text-[#0a2914]">
            {initials(who)}
          </span>
          <MoreVertical className="h-[18px] w-[18px] text-[#4a5f53]" />
        </button>
        {open && (
          <div
            role="menu"
            data-testid="account-menu"
            className="absolute right-0 top-full z-50 mt-2 w-[min(19rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-[0_18px_50px_-12px_rgba(10,41,20,0.28)]"
          >
            <div className="border-b border-gray-100 px-5 py-4">
              <p className="truncate font-display text-lg font-bold text-[#122118]" data-testid="account-menu-name">
                {who}
              </p>
              <p className="truncate text-sm text-[#4a5f53]" data-testid="account-menu-email">
                {session.email}
              </p>
            </div>
            <div className="py-2">
              {items.map(({ id, label, Icon, action }) => (
                <button key={id} type="button" role="menuitem" data-testid={`account-menu-${id}`} onClick={run(action)} className={item}>
                  <Icon className={icon} />
                  {label}
                </button>
              ))}
            </div>
            <div className="border-t border-gray-100 px-5 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#829C8D]">Langue</p>
              <div className="mt-2 inline-flex rounded-full border border-gray-200 bg-[#F7FAF8] p-1" data-testid="account-language">
                <button
                  type="button"
                  onClick={() => setLangHint(true)}
                  className="rounded-full px-4 py-1.5 text-sm font-semibold text-[#829C8D]"
                  data-testid="account-language-en"
                  title="Bientôt disponible"
                >
                  English
                </button>
                <button
                  type="button"
                  aria-pressed={lang === "fr"}
                  className="rounded-full bg-[#25D366] px-4 py-1.5 text-sm font-semibold text-[#0a2914]"
                  data-testid="account-language-fr"
                >
                  Français
                </button>
              </div>
              {langHint && <p className="mt-2 text-xs text-[#829C8D]">L’anglais arrive bientôt.</p>}
            </div>
            <div className="border-t border-gray-100 py-2">
              <button
                type="button"
                role="menuitem"
                data-testid="header-logout"
                onClick={run(onLogout)}
                className="flex w-full items-center gap-3 px-5 py-2.5 text-left text-[15px] font-medium text-[#c0392b] hover:bg-[#FDF2F1]"
              >
                <LogOut className="h-[18px] w-[18px]" />
                Se déconnecter
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountMenu;
